import { useEffect, useMemo, useState } from 'react';
import { managerApi } from '../../lib/api';
import { fmtHM } from '../../lib/format';

const STATUS_LABEL: Record<string, string> = {
  no_camarote: 'No camarote', em_descanso: 'Em descanso', nao_chegou: 'Não chegou', saiu: 'Saiu',
};
const STATUS_PILL: Record<string, string> = {
  no_camarote: 'pill-ok', em_descanso: 'pill-pending', nao_chegou: 'pill-off', saiu: 'pill-tag',
};

const REFRESH_MS = 20000;

export function AoVivoTab() {
  const [data, setData] = useState<{ date: string; employees: any[] } | null>(null);
  const [sosAlerts, setSosAlerts] = useState<any[]>([]);
  const [camarotes, setCamarotes] = useState<string[]>([]);
  const [camarote, setCamarote] = useState('todos');
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState<string | null>(null);

  function refresh() {
    return Promise.all([
      managerApi.getLiveStatus().then(setData),
      managerApi.listSosAlerts().then((res) => setSosAlerts(res.alerts.filter((a) => !a.atendidoEm))),
    ]);
  }

  async function atender(id: string) {
    setResolving(id);
    try {
      await managerApi.resolveSosAlert(id);
      setSosAlerts((prev) => prev.filter((a) => a.id !== id));
    } finally {
      setResolving(null);
    }
  }

  useEffect(() => {
    setLoading(true);
    Promise.all([refresh(), managerApi.getConfig().then((cfg) => setCamarotes(cfg.config.camarotes))]).finally(() => setLoading(false));
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  const employees = useMemo(() => {
    const all = data?.employees || [];
    return camarote === 'todos' ? all : all.filter((e) => e.camarote === camarote);
  }, [data, camarote]);

  const counts = useMemo(() => ({
    no_camarote: employees.filter((e) => e.status === 'no_camarote').length,
    em_descanso: employees.filter((e) => e.status === 'em_descanso').length,
    nao_chegou: employees.filter((e) => e.status === 'nao_chegou').length,
    saiu: employees.filter((e) => e.status === 'saiu').length,
  }), [employees]);

  if (loading || !data) return <div className="card"><p className="muted">Carregando…</p></div>;

  return (
    <>
      {sosAlerts.length > 0 && (
        <div className="card" style={{ background: 'var(--coral)', color: '#fff' }}>
          <h2 style={{ color: '#fff' }}>🚨 Pedidos de ajuda</h2>
          {sosAlerts.map((a) => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid rgba(255,255,255,.3)' }}>
              <div style={{ flex: 1, minWidth: 160 }}>
                <b>{a.nome}</b> · {a.funcao} · {a.camarote}
                {a.local && <div>📍 {a.local}</div>}
                <div style={{ fontSize: 12, opacity: 0.85 }}>{new Date(a.criadoEm).toLocaleTimeString('pt-BR')}</div>
              </div>
              {a.lat && a.lng && <a href={`https://maps.google.com/?q=${a.lat},${a.lng}`} target="_blank" rel="noreferrer" style={{ color: '#fff', textDecoration: 'underline' }}>ver no mapa</a>}
              <button className="btn btn-sm" style={{ background: '#fff', color: 'var(--coral)', border: 'none' }} disabled={resolving === a.id} onClick={() => atender(a.id)}>Marcar como atendido</button>
            </div>
          ))}
        </div>
      )}
      <div className="card">
        <div className="toolbar"><h2 style={{ margin: 0 }}>Ao vivo</h2><button className="btn btn-ghost btn-sm" onClick={refresh}>Atualizar agora</button></div>
        <p className="hint">Quem está no camarote agora, com base nas marcações de ponto de hoje. Atualiza sozinho a cada {REFRESH_MS / 1000}s.</p>
        <label>Camarote</label>
        <select value={camarote} onChange={(e) => setCamarote(e.target.value)}>
          <option value="todos">Todos os camarotes</option>
          {camarotes.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="stat-grid">
        <div className="stat-tile good"><div className="num">{counts.no_camarote}</div><div className="lbl">No camarote</div></div>
        <div className="stat-tile warn"><div className="num">{counts.em_descanso}</div><div className="lbl">Em descanso</div></div>
        <div className="stat-tile"><div className="num">{counts.nao_chegou}</div><div className="lbl">Não chegaram</div></div>
        <div className="stat-tile"><div className="num">{counts.saiu}</div><div className="lbl">Já saíram</div></div>
      </div>
      {employees.length === 0 && <p className="hint">Nenhum funcionário nesse filtro.</p>}
      {employees.map((e) => (
        <div className="card" style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }} key={e.employeeId}>
          <div style={{ flex: 'none' }}>
            {e.fotoUrl
              ? <img src={e.fotoUrl} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: '50%', border: '2px solid var(--pink)' }} />
              : <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--surface-soft)', border: '1px solid var(--line)' }} />}
          </div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontWeight: 700 }}>{e.nome}</div>
            <div className="muted">{e.funcao} · {e.camarote}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <span className={`pill ${STATUS_PILL[e.status]}`}>{STATUS_LABEL[e.status]}</span>
            {e.foraDoEsperado && <span className="pill pill-warn">⚠ fora do esperado</span>}
            <span className="muted mono" style={{ fontSize: 12 }}>
              {e.entradaEm && `entrada ${fmtHM(e.entradaEm)}`}
              {e.saidaDescansoEm && ` · descanso ${fmtHM(e.saidaDescansoEm)}`}
              {e.voltaDescansoEm && ` · volta ${fmtHM(e.voltaDescansoEm)}`}
              {e.saidaEm && ` · saída ${fmtHM(e.saidaEm)}`}
            </span>
          </div>
        </div>
      ))}
    </>
  );
}
